import { Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  BookOpen,
  Dumbbell,
  Award,
  UserCog,
  LifeBuoy,
  LogOut,
  ExternalLink,
} from "lucide-react";
import { useStudentAuth } from "@/lib/student-auth";

export const STUDENT_NAV = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "courses", label: "My Courses", icon: BookOpen },
  { id: "training", label: "Training", icon: Dumbbell },
  { id: "certification", label: "Certification", icon: Award },
  { id: "profile", label: "Profile Settings", icon: UserCog },
  { id: "help", label: "Help & Support", icon: LifeBuoy },
];

export const StudentLayout = ({ active, children }) => {
  const nav = useNavigate();
  const { student, logout } = useStudentAuth();

  const doLogout = async () => {
    await logout();
    nav("/student/login");
  };

  return (
    <main
      data-testid="student-shell"
      className="min-h-screen flex bg-[#F8FAFC] dark:bg-[#020617] text-[#1E293B] dark:text-[#F8FAFC]"
    >
      <aside
        data-testid="student-sidebar"
        className="hidden lg:flex w-60 flex-none border-r border-slate-200/70 dark:border-white/5 bg-white/70 dark:bg-[#0F172A]/60 backdrop-blur flex-col"
      >
        <Link to="/" className="flex items-center gap-2.5 px-6 py-5 border-b border-slate-200/70 dark:border-white/5">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#0F172A] text-white dark:bg-white dark:text-[#0F172A] font-display font-bold">
            S
          </span>
          <span className="leading-none">
            <span className="block font-display font-semibold tracking-tight text-[15px]">SkillPreneur</span>
            <span className="block text-[10px] uppercase tracking-[0.22em] text-slate-500">Student</span>
          </span>
        </Link>

        <nav className="flex-1 px-3 py-5 space-y-1">
          {STUDENT_NAV.map((n) => {
            const Icon = n.icon;
            const isActive = active === n.id;
            return (
              <Link
                key={n.id}
                to={`/student/${n.id}`}
                data-testid={`student-nav-${n.id}`}
                className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] shadow-sm"
                    : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5"
                }`}
              >
                <Icon className="h-4 w-4" /> {n.label}
              </Link>
            );
          })}
        </nav>

        <div className="px-4 py-4 border-t border-slate-200/70 dark:border-white/5">
          <Link
            to="/"
            className="mb-2 w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 px-3 py-2 text-xs hover:bg-slate-100 dark:hover:bg-white/5"
          >
            <ExternalLink className="h-3 w-3" /> Open site
          </Link>
          <button
            type="button"
            onClick={doLogout}
            data-testid="student-logout"
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 px-3 py-2 text-xs hover:bg-slate-100 dark:hover:bg-white/5"
          >
            <LogOut className="h-3.5 w-3.5" /> Logout
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 border-b border-slate-200/70 dark:border-white/5 bg-white/85 dark:bg-[#020617]/85 backdrop-blur">
          <div className="px-5 sm:px-8 py-3.5 flex items-center gap-3">
            <h1 className="font-display text-lg font-semibold tracking-tight">
              {STUDENT_NAV.find((n) => n.id === active)?.label || "Dashboard"}
            </h1>
            <div className="ml-auto flex items-center gap-3">
              <div className="text-right leading-tight hidden sm:block">
                <div data-testid="student-header-name" className="text-sm font-semibold">
                  {student?.name}
                </div>
                <div className="text-[11px] text-slate-500">{student?.student_id}</div>
              </div>
              <Link
                to="/student/profile"
                data-testid="student-header-settings"
                className="h-9 w-9 inline-flex items-center justify-center rounded-full border border-slate-200 dark:border-white/10 hover:bg-slate-100 dark:hover:bg-white/5"
                aria-label="Profile settings"
              >
                <UserCog className="h-4 w-4" />
              </Link>
              <button
                type="button"
                onClick={doLogout}
                data-testid="student-logout-top"
                className="h-9 w-9 lg:hidden inline-flex items-center justify-center rounded-full border border-slate-200 dark:border-white/10"
                aria-label="Logout"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="lg:hidden flex items-center gap-2 px-5 pb-3 overflow-x-auto">
            {STUDENT_NAV.map((n) => (
              <Link
                key={n.id}
                to={`/student/${n.id}`}
                data-testid={`student-nav-mobile-${n.id}`}
                className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border ${
                  active === n.id
                    ? "bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] border-transparent"
                    : "border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300"
                }`}
              >
                {n.label}
              </Link>
            ))}
          </div>
        </header>

        <div className="p-5 sm:p-8">{children}</div>
      </div>
    </main>
  );
};
